<template>
  <div @click.prevent="handleClick" :class="$mq === 'desktop' ? '' : 'Mobile-Container'">
    <component :is="chipComponent" :progression="progression" @click.prevent="onChipClicked" />
  </div>
</template>

<script>
import get from 'lodash/get'
import cloneDeep from 'lodash/cloneDeep'

import VideoManager from '@/lib/video'
import PodcastManager from '@/lib/podcast'
import to from '@/helpers/to'

import Chips, { stateToChip } from './Chip'
import CONST from './constants'

export default {
  name: 'DownloadButton',
  components: { ...Chips },
  props: {
    contentType: {
      type: String,
      required: true,
      validator: value => Object.values(CONST.AVAILABLES_CONTENTS).includes(value)
    },
    contentId: {
      type: String,
      required: true
    },
    isMain: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      state: CONST.STATE.Loading,
      progression: 0
    }
  },
  computed: {
    chipComponent() {
      return stateToChip(this.state)
    }
  },

  mounted() {
    this.computeState()
  },

  methods: {
    /**
     * Compute the state of the current button based on the props
     */
    async computeState() {
      switch (this.contentType) {
        case CONST.AVAILABLES_CONTENTS.Masterclass:
          this.state = await this.getStateForMasterclass()
          break

        case CONST.AVAILABLES_CONTENTS.LessonStep:
          this.state = await this.getStateForLessonStep()
          break

        case CONST.AVAILABLES_CONTENTS.Lesson:
          this.state = await this.getStateForLesson()
          break
      }
      this.$emit('state-change', { state: this.state, id: this.contentId })
    },
    /**
     * Get the state of the give lesson
     * @param {string} [lessonId]
     * @returns {string}
     */
    async getStateForLesson(lessonId = null) {
      const id = lessonId || this.contentId
      const lesson = get(this.$store.state, `lesson.lesson.byId.${id}`)
      let podcastDownloaded, videoDownloaded
      if (!lesson) return CONST.STATE.Error

      const vimeoId = VideoManager.getVimeoIdFromLesson(lesson)
      if (!vimeoId && !lesson.podcastUrl) return CONST.STATE.NoOffline

      if (vimeoId) videoDownloaded = await VideoManager.has(vimeoId)

      if (lesson.podcastUrl) podcastDownloaded = await PodcastManager.has(lesson.podcastUrl)

      if (videoDownloaded && podcastDownloaded) return CONST.STATE.Downloaded
      if (videoDownloaded) return CONST.STATE.Downloaded
      if (podcastDownloaded) return CONST.STATE.Downloaded

      return CONST.STATE.Ready
    },
    /**
     * Get the state of the current lesson step
     * @param {string} [lessonStepId]
     * @returns {string}
     */
    async getStateForLessonStep(lessonStepId = null) {
      const id = lessonStepId || this.contentId
      const lessonStep = get(this.$store.state, `lesson.lessonStep.byId.${id}`)

      if (!lessonStep) return CONST.STATE.Error
      const lessonStates = await Promise.all(
        lessonStep.lessons.map(el => this.getStateForLesson(el))
      )
      return this._getStateFromSubContentState(lessonStates)
    },
    /**
     * Get the state of the current masterclass
     * @param {string} [masterclassId]
     * @returns {string}
     */
    async getStateForMasterclass(masterclassId = null) {
      const id = masterclassId || this.contentId
      const masterclass = this.$store.getters.getThemeById(id)
      if (!masterclass) return CONST.STATE.Error

      const lessonStepStates = await Promise.all(
        masterclass.lessonSteps.map(el => this.getStateForLessonStep(el.id))
      )
      return this._getStateFromSubContentState(lessonStepStates)
    },
    /**
     * Handle click on the chip
     * @param {Event} event
     */
    onChipClicked() {
      if (this.state !== CONST.STATE.Downloaded) return

      this.delete()
    },
    /**
     * Get a state based on an array of subcontent state
     * @param {string[]} subcontentStates
     * @returns {string}
     */
    _getStateFromSubContentState(subcontentStates) {
      let states = cloneDeep(subcontentStates)

      // No videos
      if (states.every(state => state === CONST.STATE.NoVideo)) {
        return CONST.STATE.NoVideo
      }

      // No podcast
      if (states.every(state => state === CONST.STATE.NoPodcast)) {
        return CONST.STATE.NoPodcast
      }

      // No podcast
      if (states.every(state => state === CONST.STATE.NoOffline)) {
        return CONST.STATE.NoOffline
      }

      // Removes [No video] [No podcast] subcontent
      states = states.filter(
        state =>
          state !== CONST.STATE.NoVideo &&
          state !== CONST.STATE.NoPodcast &&
          state !== CONST.STATE.NoOffline
      )

      // All downloaded
      if (states.every(state => state === CONST.STATE.Downloaded)) {
        return CONST.STATE.Downloaded
      }
      // Partial download
      if (states.some(state => state === CONST.STATE.Downloaded)) {
        return CONST.STATE.PartialDownload
      }
      // Ready to download
      if (states.every(state => state === CONST.STATE.Ready)) {
        return CONST.STATE.Ready
      }
      // Errors
      if (states.every(state => state === CONST.STATE.Error)) {
        return CONST.STATE.Error
      }

      return CONST.STATE.Ready
    },
    async handleClick() {
      switch (this.state) {
        case CONST.STATE.PartialDownload:
          this.delete()
          break

        case CONST.STATE.Downloaded:
        case CONST.STATE.Downloading:
        case CONST.STATE.NoVideo:
        case CONST.STATE.NoPodcast:
        case CONST.STATE.NoOffline:
        case CONST.STATE.Loading:
          break

        case CONST.STATE.Ready:
          this.download()
          break

        case CONST.STATE.Error:
          await this.delete()
          await this.download()
          break
      }
    },
    /**
     * Download the current content video(s) and update the state accordingly
     */
    async download() {
      if (
        [
          CONST.STATE.Downloaded,
          CONST.STATE.Downloading,
          CONST.STATE.NoVideo,
          CONST.STATE.NoPodcast,
          CONST.STATE.NoOffline
        ].includes(this.state)
      )
        return

      this.state = CONST.STATE.Downloading

      let job

      switch (this.contentType) {
        case CONST.AVAILABLES_CONTENTS.Masterclass:
          await this.$store.dispatch('DOWNLOAD_OFFLINE_MASTERCLASS', {
            id: this.contentId,
            onProgress: this.handleDownloadProgress
          })

          break

        case CONST.AVAILABLES_CONTENTS.LessonStep:
        case CONST.AVAILABLES_CONTENTS.Lesson:
        default:
          throw new Error('Action in store to define !')
      }

      this.computeState()
    },
    /**
     * Handle the download progression
     * @param {number} progression
     */
    handleDownloadProgress(progression) {
      this.progression = progression
    },

    async delete() {
      this.state = CONST.STATE.Downloading

      let job

      switch (this.contentType) {
        case CONST.AVAILABLES_CONTENTS.Masterclass:
          job = this.$store.dispatch('DELETE_OFFLINE_MASTERCLASS', {
            id: this.contentId
          })
          break

        case CONST.AVAILABLES_CONTENTS.LessonStep:
        case CONST.AVAILABLES_CONTENTS.Lesson:
        default:
          throw new Error('Action in store to define !')
      }

      await to(job)

      this.computeState()
    }
  },
  watch: {
    state(state) {
      this.$emit('state-change', state)
    }
  }
}
</script>
